import { BrandLogo, authDirectLogout } from '@cibo/profile'
import { FriendlyError, Markdown } from '@cibo/ui'
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogTitle,
  Link as MUILink,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Navigate } from 'react-router-dom'
import { useAuth, useTermsStatus } from '../../hooks'

export const TermsForm = () => {
  const { isLoggedIn } = useAuth()
  const dispatch = useDispatch()
  const { t } = useTranslation('TermsForm')
  const [viewingVersion, setViewingVersion] = useState<'long' | 'short'>('short')

  const {
    uncompliantTerms,
    currentTerms,
    confirmTerms,
    declineTerms,
    isLoading,
    currentTermsContent,
    currentTermPriorAgreeDate,
    isError,
  } = useTermsStatus()

  if (!isLoggedIn) {
    return <Navigate replace to="/" />
  }

  if (isLoading) {
    return <CircularProgress />
  }

  if (isError) {
    return (
      <Stack spacing={2}>
        <Box p={2}>
          <FriendlyError message={t('catastrophicError')} />
        </Box>
        <DialogActions>
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ width: '100%' }}>
            <Button variant="outlined" onClick={() => dispatch(authDirectLogout())}>
              {t('logout')}
            </Button>
            <Button variant="contained" onClick={() => dispatch(authDirectLogout())}>
              {t('retry')}
            </Button>
          </Stack>
        </DialogActions>
      </Stack>
    )
  }

  if (uncompliantTerms && uncompliantTerms.length > 0 && currentTerms && currentTermsContent) {
    return (
      <Stack spacing={2}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{currentTermsContent.title}</Typography>

            <BrandLogo variant="square" size="large" />
          </Stack>
        </DialogTitle>
        <Box
          sx={{
            pl: 2,
            pr: 1,
            maxHeight: 'calc(100vh - 300px)',
            overflowX: 'hidden',
          }}
        >
          <Markdown>
            {viewingVersion === 'long'
              ? currentTermsContent.agreementMarkdownLong
              : currentTermsContent.agreementMarkdown}
          </Markdown>
          {!!currentTermsContent.agreementMarkdownLong && viewingVersion === 'short' && (
            <MUILink
              sx={{ alignSelf: 'center', lineHeight: 3 }}
              onClick={() => setViewingVersion('long')}
            >
              {t('viewFullTerms')}
            </MUILink>
          )}
          {!!currentTermsContent.agreementMarkdownLong && viewingVersion === 'long' && (
            <MUILink
              sx={{ alignSelf: 'center', lineHeight: 3 }}
              onClick={() => setViewingVersion('short')}
            >
              {t('viewAbbreviatedTerms')}
            </MUILink>
          )}
        </Box>
        <DialogActions>
          <Stack
            direction="row-reverse"
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: '100%' }}
          >
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={declineTerms}>
                {t('decline')}
              </Button>
              <Button variant="contained" onClick={confirmTerms} data-testid="accept-terms">
                {t('confirm')}
              </Button>
            </Stack>
            {currentTermPriorAgreeDate && (
              <Tooltip
                placement="top-start"
                title={
                  <Markdown>
                    {currentTermsContent.changelog
                      ? currentTermsContent.changelog
                      : t('changelogDefault', {
                          revisionDate: dayjs(currentTermsContent.revisionDate).format(
                            'MM/DD/YYYY'
                          ),
                          acceptedDate: dayjs(currentTermPriorAgreeDate).format('MM/DD/YYYY'),
                        })}
                  </Markdown>
                }
              >
                <Button color="secondary" variant="text">
                  {t('whyAmISeeingThis')}
                </Button>
              </Tooltip>
            )}
          </Stack>
        </DialogActions>
      </Stack>
    )
  }

  return null
}
